import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Container, Typography, List, ListSubheader, ListItemButton, ListItemText, Paper, Button, Chip,
  FormControl, InputLabel, Select, MenuItem, useMediaQuery, Alert
} from '@mui/material';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import LockIcon from '@mui/icons-material/Lock';
import axios from 'axios';
import SideMenu from '../../components/SideMenu';
import { useUser } from '../../context/UserContext';
import { settingsAccess } from '../../utils/settingsAccess';
import { getOwnerToken, getOwnerExpiresAt, clearOwnerUnlock, ownerHeader, isOwnerLockedError } from '../../utils/ownerSession';
import UnlockDialog from '../../owner/UnlockDialog';
import HowItWorks from '../../owner/components/HowItWorks';
import CustomerApp from '../../owner/CustomerApp';
import AdsOffers from '../../owner/AdsOffers';
import Affiliates from '../../owner/Affiliates';
import Partners from '../../owner/Partners';
import PartnerCompanies from '../../owner/PartnerCompanies';
import CompanySetup from '../../owner/CompanySetup';
import Hiring from '../../owner/Hiring';
import ContractTemplates from '../../owner/ContractTemplates';
import Referrals from '../../owner/Referrals';
import Knights from '../../owner/Knights';
import Notifications from '../../owner/Notifications';
import { usePageTour } from '../../tours/usePageTour';

// Wording below is checked against the code it describes: wkroadss.com reads the live
// catalog, postings, ad settings and affiliate program on every page load (site.js,
// affiliates.js; the API caches for 2–5 minutes), the customer app re-reads its config
// after 5 minutes, and the droplet's daily run (scripts/publish-cron.sh, 06:10 UTC)
// only re-bakes files already on the server — it never fetches new portal settings.
const BAKE_NOTE = 'The baked copy of the site (what search engines read, and the fallback when the portal is unreachable) only changes when you run "node scripts/build.mjs" then "node scripts/bake.mjs" in the site folder on the PC and upload the site. The droplet\'s daily 6:10 UTC run (about 1 AM Central) re-bakes what is already on the server to publish scheduled blog posts; it does not fetch new settings.';
const APP_NOTE = 'The customer app re-reads its settings when it opens and about every 5 minutes after that.';

const SECTIONS = [
  {
    group: 'Company',
    items: [
      {
        key: 'company/setup', label: 'Company setup',
        render: () => <CompanySetup />,
        help: {
          controls: 'Your company name, logo, colors, About text and reviews, services and prices, published agreements (provider agreement, code of conduct, privacy policy, customer terms, SMS terms), provider requirements, social links and app links.',
          shows: ['Portal: name, logo and colors on every page.', 'Customer website and app: name, logo, colors, About, reviews, the published documents.', 'Provider application form: your requirements and your agreement and code of conduct.', 'Emails, texts and PDFs: your name, support contact and logo.'],
          after: 'Branding, About and requirements take effect within about a minute. Publishing a document creates a new version immediately; applications record the version they accepted.'
        }
      }
    ]
  },
  {
    group: 'Website',
    items: [
      {
        key: 'website/services', label: 'Services & pricing',
        render: () => <CustomerApp view="services" />,
        help: {
          controls: 'Which roadside services are offered, their prices by vehicle class and region, whether each one accepts cash, and how requests are dispatched (nearest driver or a blast to every driver in range).',
          shows: ['wkroadss.com: the order page and every "Request service" button.', 'Customer app: the services grid and every quote.'],
          after: ['Prices and the service list go live within about 5 minutes on wkroadss.com and in the app.', APP_NOTE, BAKE_NOTE]
        }
      },
      {
        key: 'website/memberships', label: 'Memberships',
        render: () => <CustomerApp view="memberships" />,
        help: {
          controls: 'The membership switch, the six plans (names, prices, what is included) and the introductory offer. Saving also updates the matching prices and coupons in Stripe — the Stripe status card shows whether that worked.',
          shows: ['wkroadss.com: the plan pages and the join flow.', 'Customer app: Account → Membership.'],
          after: ['Plan prices and the intro offer go live within about 5 minutes on wkroadss.com and in the app; a member already on a plan keeps their current price until they switch.', APP_NOTE, BAKE_NOTE]
        }
      },
      {
        key: 'website/customers', label: 'Customers & claims',
        render: () => <CustomerApp view="customers" />,
        help: {
          controls: 'Look up a customer (plan, vehicles, requests, payments) and decide reimbursement claims.',
          shows: ['Customer app: the customer sees their membership and claim status under Account.', 'Email: a claim decision emails the customer.'],
          after: 'Changes take effect immediately; nothing on the website changes.'
        }
      },
      {
        key: 'website/ads', label: 'Ads & Offers', platformOnly: true,
        render: () => <AdsOffers />,
        help: {
          controls: 'Google AdSense on the blog (publisher ID, ad units, on/off) and the "Be ready next time" product cards with your Amazon Associates tag.',
          shows: ['wkroadss.com: AdSense runs on blog pages only; product cards appear on the order page when a customer backs out after seeing the price.', 'Customer app: the same product cards after a quote is abandoned or a request is cancelled.'],
          after: ['Product cards and ad-unit IDs are read live (within about 2 minutes). The AdSense script tag itself is written into the blog pages by the bake, so switching AdSense on or off also needs the re-bake and upload below before Google starts or stops serving.', APP_NOTE, BAKE_NOTE]
        }
      },
      {
        key: 'website/affiliates', label: 'Affiliates',
        render: () => <Affiliates />,
        help: {
          controls: 'The affiliate program switch, whether applications are open, the commission and how long renewals keep earning, applications, approved affiliates and their commissions.',
          shows: ['wkroadss.com/affiliates: the program numbers and the application form.', 'Customer app and the order page: the referral-code field at sign-up.', 'Email: applicants get a receipt and a decision; support gets each new application.'],
          after: ['The affiliates page reads the program live, so the switches and numbers show within about 5 minutes. Turning the program off hides the affiliate section and the code field; existing affiliates and unpaid commissions are kept.', APP_NOTE, BAKE_NOTE]
        }
      },
      {
        key: 'website/partners', label: 'Integration partners (API)', platformOnly: true,
        render: () => <Partners />,
        help: {
          controls: 'White-label partners (vehicle and insurance companies) that send cases through the Partner API: their branding, API keys and volume.',
          shows: ['Partner API: each partner\'s key and branding. Nothing on wkroadss.com or in the customer app.'],
          after: 'Takes effect immediately. A new or rotated API key is shown once — copy it before closing the dialog.'
        }
      }
    ]
  },
  {
    group: 'Notifications',
    items: [
      {
        key: 'notifications/switchboard', label: 'Switchboard',
        render: () => <Notifications view="switchboard" />,
        help: {
          controls: 'Every automated text, email, phone push and portal-bell message the platform sends, with a master switch per channel, a switch per message, the channels each message may use, and for staff messages which roles may receive it.',
          shows: ['Customers: request received, driver assigned, on the way, arrived, complete, the performance-review text survey, cancellations.', 'Staff and providers: job offers, status changes, GOA decisions, customer replies.', 'Hiring: interview reminders, owner alerts, applicant emails.'],
          after: 'Takes effect on the next message sent. Each staff member\'s own choices under Edit Profile still apply underneath these switches.'
        }
      },
      {
        key: 'notifications/compose', label: 'Send a notification',
        render: () => <Notifications view="compose" />,
        help: {
          controls: 'Write a message and send it to staff (portal bell, phone push, email) or customers (phone push, email), to everyone, by role, or to people you pick.',
          shows: ['Portal: under the bell icon.', 'Driver app and customer app: in the phone\'s notification bar.', 'Email: as a plain message from the usual sender.'],
          after: 'Delivered immediately; the list below shows how many got it.'
        }
      },
      {
        key: 'notifications/stoplist', label: 'STOP list',
        render: () => <Notifications view="stoplist" />,
        help: {
          controls: 'Phone numbers that replied STOP to the texting number (added automatically) or that you add by hand. Nothing is ever texted to them.',
          shows: ['Customer app: a customer who texted STOP has "Text alerts" switched off.'],
          after: 'Immediate. Texting START puts a number back; removing one here does the same.'
        }
      }
    ]
  },
  {
    group: 'Partners',
    items: [
      {
        key: 'partners/companies', label: 'Partner companies', platformOnly: true,
        render: () => <PartnerCompanies />,
        help: {
          controls: 'Partner motor clubs that run their own copy of the platform: create one (its owner gets a welcome email with a one-time setup link), suspend or reactivate it, switch demo mode, set custom domains, resend the setup link, and "act as" a company for 30 minutes to see and change its data as yourself (a band at the top shows who you are acting as; Stop returns you here). Each row shows its people, customers and jobs; Platform billing below shows card volume and White Knight\'s share.',
          shows: ['Their portal at <name>.wkmc.services, their customer site at <name>-roadside.wkmc.services, and their provider application form. Nothing on wkroadss.com.'],
          after: 'A new company exists immediately with White Knight\'s services, prices and settings copied in (no Stripe ids); its owner finishes branding, agreements and payments in the setup wizard.'
        }
      }
    ]
  },
  {
    group: 'Hiring',
    items: [
      {
        key: 'hiring/postings', label: 'Job postings',
        render: () => <Hiring view="postings" />,
        help: {
          controls: 'The open positions by city, their pay wording, requirements and tools, plus the pipeline settings: how long after applying the assessment invite goes out, the passing score, and the phone-interview email template.',
          shows: ['The hiring site (whiteknightroadsideservices.com): one listing per posting, with "Apply now". It has no link back to wkroadss.com, so applicants never see service prices.', 'Email: the assessment invite and the interview email go to applicants.'],
          after: ['The careers page reads postings live, so a new or paused posting shows within a few minutes. Pipeline settings apply to the next applicant.', BAKE_NOTE]
        }
      },
      {
        key: 'hiring/applicants', label: 'Applicants',
        render: () => <Hiring view="applicants" />,
        help: {
          controls: 'Every application: résumé, background and driving answers, Technician Skills Assessment scores and videos, and the status you move them to (interview, hired, rejected).',
          shows: ['Email: moving an applicant to "Interview scheduled" sends the interview email; the assessment invite is sent automatically after the delay in Job postings → Pipeline settings.'],
          after: 'Status changes take effect immediately. Nothing here appears on the website.'
        }
      },
      {
        key: 'hiring/contracts', label: 'Contract templates',
        render: () => <ContractTemplates />,
        help: {
          controls: 'The contracts new hires sign: upload a PDF once, mark where they sign, initial, date or type, and send that same document to any hire from their applicant panel.',
          shows: ['Email + wkroadss.com: the hire gets a link, fills the marked spots on their phone and draws their signature; both of you get the signed copy.'],
          after: 'Changes to a template apply to contracts sent after the change; contracts already sent keep the version they were sent with.'
        }
      },
      {
        key: 'hiring/reviewer', label: 'Assessment reviewer link',
        render: () => <Hiring view="reviewer" />,
        help: {
          controls: 'A private link that lets someone read every module of the Technician Skills Assessment — slides, pictures, quiz answers and the video tasks — and flag mistakes. Nothing they do counts as an application.',
          shows: ['wkroadss.com: only for whoever has the link. Flags land in the list on this page and in your email.'],
          after: 'The link works as soon as you create it, until you make a new one or turn it off.'
        }
      }
    ]
  },
  {
    group: 'Programs',
    items: [
      {
        key: 'programs/knights', label: 'Customer referrals (Knights)', platformOnly: true,
        render: () => <Knights />,
        help: {
          controls: 'The Knights program switch, how many Knights a friend joining, a first paid service and a first paid membership earn, the diminishing rate for each generation down the referral chain, the wording that explains how Knights are used, and recording redemptions.',
          shows: ['Customer app: Account → Refer friends (share code, link, balance, history) and the referral-code field at sign-up.', 'wkroadss.com: a share link (wkroadss.com/?ref=CODE) credits the sharer when the visitor orders or signs up.'],
          after: ['Awards happen automatically as referred customers sign up, pay for a call or pay a membership invoice; each one is paid once per referred customer. Redemptions are recorded here by hand after you honour them.', APP_NOTE]
        }
      },
      {
        key: 'programs/referrals', label: 'Provider referrals', platformOnly: true,
        render: () => <Referrals />,
        help: {
          controls: 'The provider-refers-a-provider bonus: who referred whom, progress toward the completed-jobs threshold, and marking a bonus paid.',
          shows: ['Provider application form: the "referred by" vendor number. Nothing on wkroadss.com.'],
          after: 'Qualification is checked automatically as jobs complete; "Mark paid" records a payment you made outside the portal.'
        }
      }
    ]
  }
];
const DEFAULT_KEY = 'website/services'; // the console still opens on Services & pricing

const fmtRemaining = (expiresAt) => {
  if (!expiresAt) return '';
  const mins = Math.max(0, Math.round((expiresAt - Date.now()) / 60000));
  return mins <= 1 ? 'under a minute' : `${mins} min`;
};

/**
 * Owner Console: the website, hiring and marketing tools in one place, for
 * Org 1 owners only, behind a second password check (see utils/ownerSession).
 */
export default function OwnerConsole() {
  const isSmallScreen = useMediaQuery('(max-width:600px)');
  const navigate = useNavigate();
  const { '*': splat } = useParams();
  const { currentUser } = useUser();
  const allowed = useMemo(() => settingsAccess(currentUser).ownerConsole, [currentUser]);
  // Partner companies, the integration-partner API, Ads & Offers and the two referral
  // programs belong to White Knight only; a partner owner sees every other section
  // for their own company (the matching routes are gated server-side as well)
  const isPlatform = (currentUser?.orgKind || 'platform') === 'platform';
  const sections = useMemo(
    () => SECTIONS.map((g) => ({ ...g, items: g.items.filter((i) => !i.platformOnly || isPlatform) })).filter((g) => g.items.length > 0),
    [isPlatform]
  );
  const allItems = useMemo(() => sections.flatMap((g) => g.items), [sections]);
  const companyName = currentUser?.organization?.name || 'White Knight';

  const activeKey = allItems.some((i) => i.key === splat) ? splat : DEFAULT_KEY;
  const active = allItems.find((i) => i.key === activeKey);
  useEffect(() => {
    if (splat !== activeKey) navigate(`/owner/${activeKey}`, { replace: true });
  }, [splat, activeKey, navigate]);

  // Unlock state: the dialog opens on arrival when locked, and whenever a request comes back OWNER_LOCKED
  const [unlocked, setUnlocked] = useState(() => !!getOwnerToken());
  const [expiresAt, setExpiresAt] = useState(() => getOwnerExpiresAt());
  const [dialogOpen, setDialogOpen] = useState(() => !getOwnerToken());
  const [mountKey, setMountKey] = useState(0); // re-mount the section after an unlock so it reloads
  const [, setTick] = useState(0); // re-render every 30 s so the "time left" chip counts down
  // The first-run tour waits for the unlock, so it never sits on top of the password dialog
  usePageTour('owner-console', allowed && unlocked);

  const refreshLock = useCallback(() => {
    const token = getOwnerToken();
    setUnlocked(!!token);
    setExpiresAt(getOwnerExpiresAt());
    if (!token) setDialogOpen(true);
  }, []);

  useEffect(() => {
    const id = setInterval(() => { setTick((t) => t + 1); refreshLock(); }, 30000);
    return () => clearInterval(id);
  }, [refreshLock]);

  // Any console request that comes back OWNER_LOCKED opens the dialog, then retries once unlocked
  useEffect(() => {
    const pending = [];
    const id = axios.interceptors.response.use(undefined, (error) => {
      if (!isOwnerLockedError(error) || error.config?.__ownerRetried) return Promise.reject(error);
      setUnlocked(false);
      setDialogOpen(true);
      return new Promise((resolve, reject) => {
        pending.push({ error, resolve, reject });
      });
    });
    const onUnlock = () => {
      while (pending.length) {
        const { error, resolve, reject } = pending.shift();
        const cfg = { ...error.config, __ownerRetried: true, headers: { ...(error.config.headers || {}), ...ownerHeader() } };
        axios.request(cfg).then(resolve, reject);
      }
    };
    window.addEventListener('owner-unlocked', onUnlock);
    return () => {
      axios.interceptors.response.eject(id);
      window.removeEventListener('owner-unlocked', onUnlock);
      pending.splice(0).forEach(({ error, reject }) => reject(error));
    };
  }, []);

  const handleUnlocked = () => {
    setDialogOpen(false);
    refreshLock();
    window.dispatchEvent(new Event('owner-unlocked'));
    setMountKey((k) => k + 1);
  };

  const lockNow = () => {
    clearOwnerUnlock();
    setUnlocked(false);
    setExpiresAt(null);
    setDialogOpen(true);
  };

  if (!allowed) {
    return (
      <>
        <SideMenu />
        <Container maxWidth="sm" sx={{ mt: 6 }}><Alert severity="warning">The Owner Console is for {companyName} owners only.</Alert></Container>
      </>
    );
  }

  const nav = isSmallScreen ? (
    <FormControl fullWidth size="small" sx={{ mb: 2 }}>
      <InputLabel>Section</InputLabel>
      <Select label="Section" value={activeKey} onChange={(e) => navigate(`/owner/${e.target.value}`)}>
        {sections.flatMap((g) => [
          <ListSubheader key={`h-${g.group}`}>{g.group}</ListSubheader>,
          ...g.items.map((i) => <MenuItem key={i.key} value={i.key}>{i.label}</MenuItem>)
        ])}
      </Select>
    </FormControl>
  ) : (
    <Paper variant="outlined" sx={{ width: 240, flexShrink: 0, alignSelf: 'flex-start', position: 'sticky', top: 16 }} data-tour="owner-nav">
      <List dense disablePadding>
        {sections.map((g) => (
          <React.Fragment key={g.group}>
            <ListSubheader disableSticky sx={{ lineHeight: '36px', fontWeight: 700 }}>{g.group}</ListSubheader>
            {g.items.map((i) => (
              <ListItemButton key={i.key} selected={i.key === activeKey} onClick={() => navigate(`/owner/${i.key}`)} sx={{ pl: 3 }} data-tour={`owner-nav-${i.key}`}>
                <ListItemText primary={i.label} primaryTypographyProps={{ variant: 'body2' }} />
              </ListItemButton>
            ))}
          </React.Fragment>
        ))}
      </List>
    </Paper>
  );

  return (
    <>
      <SideMenu />
      <Container maxWidth={false}>
        <Box mt={4} mb={2} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
          <Box>
            <Typography variant="h4">Owner Console</Typography>
            <Typography variant="body2" color="text.secondary">Website, marketing and hiring — owners only.</Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={1} data-tour="owner-lock">
            {unlocked ? (
              <>
                <Chip icon={<LockOpenIcon />} color="success" variant="outlined" size="small" label={`Unlocked · ${fmtRemaining(expiresAt)} left`} />
                <Button size="small" startIcon={<LockIcon />} onClick={lockNow}>Lock now</Button>
              </>
            ) : (
              <Button size="small" variant="contained" startIcon={<LockOpenIcon />} onClick={() => setDialogOpen(true)}>Unlock</Button>
            )}
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start', flexDirection: isSmallScreen ? 'column' : 'row' }}>
          {nav}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box data-tour="owner-howitworks"><HowItWorks sectionKey={active.key} help={active.help} /></Box>
            <Box data-tour="owner-section">
              {unlocked
                ? <React.Fragment key={`${active.key}-${mountKey}`}>{active.render()}</React.Fragment>
                : <Alert severity="info">Unlock the console to see this section.</Alert>}
            </Box>
          </Box>
        </Box>
      </Container>

      <UnlockDialog open={dialogOpen} onUnlocked={handleUnlocked} onCancel={unlocked ? () => setDialogOpen(false) : undefined} />
    </>
  );
}
